import React, { useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ExternalLink,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { SalesRequestDebt } from "~/hooks/useNewDebtSummary";
import { supabase } from "~/integrations/supabase/client";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface SalesNotesTableProps {
  salesRequests: SalesRequestDebt[];
  cutoffDate?: Date;
}

type SortField =
  | "factura_number"
  | "created_at"
  | "payment_due_date"
  | "original_amount"
  | "debt_amount_at_cutoff"
  | "days_to_due_at_cutoff";
type SortDirection = "asc" | "desc";

export const SalesNotesTable: React.FC<SalesNotesTableProps> = ({
  salesRequests,
  cutoffDate,
}) => {
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc"); // Show oldest first
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // State for OrderDetailDialog
  const [selectedOrder, setSelectedOrder] = useState<SalesRequest | null>(null);
  const [items, setItems] = useState<SalesRequestItem[]>([]);

  // Sorting and pagination logic
  const sortedAndPaginatedData = useMemo(() => {
    const sorted = [...salesRequests].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle different data types
      if (sortField === "created_at" || sortField === "payment_due_date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return {
      data: sorted.slice(startIndex, endIndex),
      totalPages: Math.ceil(sorted.length / itemsPerPage),
      totalItems: sorted.length,
    };
  }, [salesRequests, sortField, sortDirection, currentPage, itemsPerPage]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) {
      return <ChevronsUpDown className="h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      accepted: { label: "Aceptado", variant: "default" as const },
      in_transit: { label: "En Tránsito", variant: "secondary" as const },
      delivered: { label: "Entregado", variant: "outline" as const },
      pending: { label: "Pendiente", variant: "destructive" as const },
      prepaid: { label: "Prepagado", variant: "default" as const },
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || {
      label: status,
      variant: "outline" as const,
    };

    return (
      <Badge variant={statusInfo.variant} className="text-xs">
        {statusInfo.label}
      </Badge>
    );
  };

  const getDaysInfo = (daysTo: number, cutoffDate?: Date) => {
    const referenceDate = cutoffDate ? dayjs(cutoffDate) : dayjs();
    const isHistorical = !!cutoffDate;

    if (daysTo < 0) {
      const daysOverdue = Math.abs(daysTo);
      return (
        <span className="text-xs text-red-600">
          {isHistorical ? "Estaba " : ""}Vencido: {daysOverdue} días
        </span>
      );
    } else if (daysTo < 5) {
      const label =
        daysTo === 0
          ? isHistorical
            ? "Vencía hoy"
            : "Vence hoy"
          : `${isHistorical ? "Vencía en" : "Vence en"}: ${daysTo} días`;
      return <span className="text-xs text-orange-600">{label}</span>;
    } else {
      return (
        <span className="text-xs text-green-600">
          {isHistorical ? "Vencía en" : "Vence en"}: {daysTo} días
        </span>
      );
    }
  };

  // Fetch full sales request data for OrderDetailDialog
  const fetchSalesRequestById = async (salesRequestId: string) => {
    try {
      const { data, error } = await supabase
        .from("sales_request")
        .select("*")
        .eq("id", salesRequestId)
        .single();

      if (error) {
        console.error("Error fetching sales request:", error);
        toast({
          title: "Error",
          description: "No se pudo cargar la información de la nota de venta",
          variant: "destructive",
        });
        return null;
      }

      return data as SalesRequest;
    } catch (error) {
      console.error("Error fetching sales request:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la información de la nota de venta",
        variant: "destructive",
      });
      return null;
    }
  };

  // Fetch items for the selected sales request
  const fetchItemsForSalesRequest = async (salesRequestId: string) => {
    try {
      const { data, error } = await supabase
        .from("sales_request_items")
        .select("*")
        .eq("sales_request", salesRequestId);

      if (error) {
        console.error("Error fetching items:", error);
        setItems([]);
        return;
      }

      setItems(data as SalesRequestItem[]);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
    }
  };

  // Handle row click to open OrderDetailDialog
  const handleRowClick = async (salesRequestDebt: SalesRequestDebt) => {
    const fullSalesRequest = await fetchSalesRequestById(salesRequestDebt.id);
    if (fullSalesRequest) {
      setSelectedOrder(fullSalesRequest);
      fetchItemsForSalesRequest(fullSalesRequest.id);
    }
  };

  // Helper functions for OrderDetailDialog (simplified versions)
  const getSellerInfo = (sellerId: string) => {
    // This should be replaced with actual seller data fetching if needed
    return {
      name: "Vendedor",
      lastname: "",
      email: "",
    };
  };

  const clientNames: Record<string, string> = {};

  if (salesRequests.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center text-gray-500">
        No hay notas de venta pendientes para este cliente
        {cutoffDate && (
          <span className="mt-1 block text-sm">en la fecha seleccionada</span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">
            Notas de Venta Pendientes ({salesRequests.length}) - Total: $
            {formatCurrency(
              salesRequests.reduce(
                (sum, sr) => sum + sr.debt_amount_at_cutoff,
                0
              )
            )}
          </h4>
          {salesRequests.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(`/clients/${salesRequests[0].client_id}`, "_blank")
              }
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Ver Cliente
            </Button>
          )}
        </div>
        {cutoffDate && (
          <p className="mt-1 text-xs text-gray-600">
            Deuda calculada al {dayjs(cutoffDate).format("DD/MM/YYYY")}
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("factura_number")}
                >
                  No Factura
                  {getSortIcon("factura_number")}
                </Button>
              </TableHead>
              <TableHead className="w-20">Estado</TableHead>
              <TableHead className="w-24">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("created_at")}
                >
                  Fecha Creación
                  {getSortIcon("created_at")}
                </Button>
              </TableHead>
              <TableHead className="w-24">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("payment_due_date")}
                >
                  Fecha Vencimiento
                  {getSortIcon("payment_due_date")}
                </Button>
              </TableHead>
              <TableHead className="w-24 text-right">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("original_amount")}
                >
                  Monto Total
                  {getSortIcon("original_amount")}
                </Button>
              </TableHead>
              <TableHead className="w-24 text-right">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("debt_amount_at_cutoff")}
                >
                  Deuda Pendiente
                  {getSortIcon("debt_amount_at_cutoff")}
                </Button>
              </TableHead>
              <TableHead className="w-28">
                <Button
                  variant="ghost"
                  className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
                  onClick={() => handleSort("days_to_due_at_cutoff")}
                >
                  Vencimiento
                  {getSortIcon("days_to_due_at_cutoff")}
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAndPaginatedData.data.map((request, index) => (
              <TableRow
                key={request.id}
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-gray-50"
                onClick={() => handleRowClick(request)}
              >
                <TableCell className="p-2 font-mono text-xs">
                  {request.factura_number}
                </TableCell>
                <TableCell className="p-2">
                  {getStatusBadge(request.status)}
                </TableCell>
                <TableCell className="p-2 text-xs">
                  {dayjs(request.created_at).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="p-2 text-xs">
                  {dayjs(request.payment_due_date).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="p-2 text-right text-xs font-medium">
                  ${formatCurrency(request.original_amount)}
                  <div className="text-xs text-gray-500">Total con IVA</div>
                </TableCell>
                <TableCell className="p-2 text-right text-xs font-medium text-red-600">
                  ${formatCurrency(request.debt_amount_at_cutoff)}
                  {request.payments_before_cutoff > 0 && (
                    <div className="text-[10px] text-green-600">
                      Pagado: ${formatCurrency(request.payments_before_cutoff)}
                    </div>
                  )}
                  {request.has_checks_in_portfolio && (
                    <div className="text-[10px] text-blue-600">
                      Cheque en cartera: $
                      {formatCurrency(request.checks_in_portfolio_amount)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="p-2">
                  {getDaysInfo(request.days_to_due_at_cutoff, cutoffDate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {sortedAndPaginatedData.totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-4 py-3">
          <div className="text-sm text-gray-600">
            Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
            {Math.min(
              currentPage * itemsPerPage,
              sortedAndPaginatedData.totalItems
            )}{" "}
            de {sortedAndPaginatedData.totalItems} registros
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              {Array.from(
                { length: Math.min(5, sortedAndPaginatedData.totalPages) },
                (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="h-8 w-8 p-0"
                    >
                      {page}
                    </Button>
                  );
                }
              )}
              {sortedAndPaginatedData.totalPages > 5 && (
                <>
                  {currentPage < sortedAndPaginatedData.totalPages - 2 && (
                    <span className="text-gray-400">...</span>
                  )}
                  <Button
                    variant={
                      currentPage === sortedAndPaginatedData.totalPages
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    onClick={() =>
                      setCurrentPage(sortedAndPaginatedData.totalPages)
                    }
                    className="h-8 w-8 p-0"
                  >
                    {sortedAndPaginatedData.totalPages}
                  </Button>
                </>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === sortedAndPaginatedData.totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog
          selectedOrder={selectedOrder}
          setSelectedOrder={setSelectedOrder}
          getSellerInfo={getSellerInfo}
          clientNames={clientNames}
          items={items}
        />
      )}
    </div>
  );
};
